/*
 * Copyright (c) 2020 Honeywell International, Inc. All rights reserved.
 *
 * This file contains trade secrets of Honeywell International, Inc.  No part
 *  may be reproduced or transmitted in any form by any means or for any
 *  purpose without the express written permission of Honeywell.
 *
 */
import React, {ReactElement} from 'react';
import AuthenticationContext, {UserInfo} from "adal-angular";
import GlobalStore from "../../stores/GlobalStore/GlobalStore";
import {ReactNode, useCallback, useEffect, useMemo, useState} from "react";

export type LoginFunction = () => void;
export type LogoutFunction = () => void;

/**
 * The properties for Forge Connect identity.
 */
export interface IForgeConnectIdentityProps {

  provider: AuthenticationContext;

  globalStore?: GlobalStore;

  logOutUri?: string;
}

/**
 * The function props that should be used by child component
 */
export interface IForgeConnectFunctionProps {
  login: LoginFunction;
  logout: LogoutFunction;
  userInfo: AuthenticationContext.UserInfo;
  error: string;
  isAuthenticated: boolean
}

/**
 *  This represents the higher order component for all the Forge components
 *  that need authentication using ADAL.
 *
 * @constructor
 */
  //@Todo Once Forge Identity and access are adopted, we need to leverage them for providing this HOC functionality
export const ForgeConnectIdentity: React.FunctionComponent<IForgeConnectIdentityProps> = props => {

    const {globalStore} = props;
    const authContext = props.provider;

    // Initialize the state
    const [ready, _setIsReady] = useState(false);
    const [isAuthenticated, _setIsAuthenticated] = useState(false);
    const [userInfo, _setUserInfo] = useState({} as AuthenticationContext.UserInfo);
    const [error, _setError] = useState("");

    //// Wrap the setters in callback hooks, so that we can control the dependencies that trigger the set
    /**
     * Set the 'isReady' flag
     * @type {(newReady: boolean) => void}
     */
    const setIsReady = useCallback(
      (newReady: boolean) => {
        _setIsReady(newReady)
      },
      [ready],
    );

    const setUserInfo = useCallback(
      (newUserInfo: UserInfo) => {
        _setUserInfo(newUserInfo)
      },
      [userInfo],
    );

    const setIsAuthenticated = useCallback(
      (newIsAuthenticated: boolean) => {
        _setIsAuthenticated(newIsAuthenticated)
      },
      [isAuthenticated],
    );

    const setError = useCallback(
      (newError: any) => {
        if (newError === 'login_required') {
          authContext.login();
        } else {
          _setError(newError);
        }
      },
      [error],
    );

    // Set the logout URL if provided
    if (!!props.logOutUri) {
      authContext.config.logOutUri = props.logOutUri;
    }
    if (authContext.isCallback(window.location.hash)) {
      authContext.handleWindowCallback();
    }

    /**
     * The login function
     * @type {() => void}
     */
    const login = useCallback(() => {
      authContext.login();
    }, [authContext]);

    /**
     * The logout function
     * @type {() => void}
     */
    const logout = useCallback(() => {
      authContext.logOut();
    }, [authContext]);
    /**
     * Check if the request is an auth callback (This is currently proprietary for ADAL)
     * @returns {boolean}
     */
    const isAuthCallback = useCallback(() => {
      return (window === window.parent) && window === window.top && !authContext.isCallback(window.location.hash);
    }, [authContext]);
    /**
     * The hook post mount
     */
    useEffect(() => {
      let envVariables = globalStore!.envVariables;
      let authCallback = isAuthCallback();
      if (authCallback) {
        let user = authContext.getCachedUser();
        if (!user) {
          login();
          user = authContext.getCachedUser();
        }
        authContext.acquireToken(envVariables.AD_AUDIENCE, (errorDesc: string | null,
                                                            token: string | null,
                                                            error: any) => {
          if (!!error) {
            setError(error);
            setIsAuthenticated(false);
          } else {
            setUserInfo(user);
            setIsAuthenticated(true);
          }
          setIsReady(true);
        });
      }
    }, [authContext]);

    // The authentication data to be passed to the children() if it's a function
    const childrenFunctionProps = useMemo<IForgeConnectFunctionProps>(
      () => ({
        isAuthenticated,
        userInfo,
        error,
        login,
        logout
      }),
      [isAuthenticated, userInfo, error, login, logout],
    );

    /**
     * @param children
     * @param childrenProps
     *
     */
    const getFunctionComponent = (children: ReactNode, childrenProps: IForgeConnectFunctionProps) => {
      if (children) {
        let functionComponent = children as ((props: IForgeConnectFunctionProps) => ReactElement);
        return functionComponent(childrenProps);
      } else {
        return <></>;
      }
    }

    /**
     * return the function component
     */
    return (ready) ? getFunctionComponent(props.children, childrenFunctionProps) : <></>;
  }

